import { useFieldArray, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Trash2, Plus, Info } from "lucide-react";
import type { EventFormData } from "../../schemas/eventSchema";

export function FaqManager() {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<EventFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "config.faqs",
  });

  function handleAddFaq() {
    append({
      id: crypto.randomUUID(),
      question: "",
      answer: "",
    });
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button
          onClick={handleAddFaq}
          startIcon={<Plus size={20} />}
          variant="outlined"
        >
          Add Question
        </Button>
        <Tooltip
          title='Frequently asked questions appear in a dedicated FAQ screen on the mobile app. Add the kinds of questions attendees ask before or during the event — dress code, parking, Wi-Fi password, dietary policies, agenda overview. Questions display in the order shown here. Answers can include links using [label](https://example.com).'
          arrow
          placement="top"
          slotProps={{ tooltip: { sx: { fontSize: "1rem", maxWidth: 360, p: 1.5 } } }}
        >
          <IconButton aria-label="FAQ section info">
            <Info size={22} />
          </IconButton>
        </Tooltip>
      </Box>

      {fields.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No FAQs added yet. Click "Add Question" to create one.
        </Typography>
      )}

      {fields.map((field, index) => (
        <Paper key={field.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              {...register(`config.faqs.${index}.question`)}
              label="Question"
              required
              fullWidth
              error={!!errors.config?.faqs?.[index]?.question}
              helperText={errors.config?.faqs?.[index]?.question?.message}
            />
            <IconButton
              onClick={() => remove(index)}
              aria-label="Remove FAQ entry"
              color="error"
              sx={{ mt: 0.5 }}
            >
              <Trash2 size={20} />
            </IconButton>
          </Box>

          <TextField
            {...register(`config.faqs.${index}.answer`)}
            label="Answer"
            required
            fullWidth
            multiline
            minRows={3}
            error={!!errors.config?.faqs?.[index]?.answer}
            helperText={
              errors.config?.faqs?.[index]?.answer?.message ??
              "Links: [Open Uber voucher](https://r.uber.com/code)"
            }
          />
        </Paper>
      ))}
    </Box>
  );
}
